import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import BackButton from "../../../components/BackButton";
import Card from "../../../components/Card";
import CustomButton from "../../../components/CustomButton";
import SearchInput from "../../../components/CustomSearchInput";
import SubMenu from "../../../components/SubMenu";
import Layout from "../../../layout";
import { getInviteAction } from "../../../redux/actions/inviteAction";
import { useDispatch, useSelector } from "react-redux";
import { RESET_INVITES, ERROR } from "../../../redux/actionTypes";
import EmptyState from "../../../components/EmptyState";
import { ButtonLoader } from "../../../components/ButtonLoader";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import { useTourGuide } from "../../../hooks/useTourGuide";
import {
  ImportContactsModal,
  NdpaConsentModal,
  NetworkGrowthSummary,
} from "../../../components/GrowNetwork";
import CustomModal from "../../../components/CustomModal";
import contactImportService from "../../../services/contactImportService";
import {
  trackGrowNetworkCardClick,
  trackContactsImportCompleted,
  trackInviteCopyLinkClick,
} from "../../../utils/analytics";
import cogoToast from "cogo-toast";
import "../../../components/GrowNetwork/index.scss";

const subMenuOptions = [
  ["Unique Invite Link", "/invite/link"],
  ["Contacts", "/invite/contacts"],
  ["invitees", "/invite/invitees"],
];

const CONTACTS_PREVIEW_KEY = "wb_contacts_import_preview";

const Invite = () => {
  useTourGuide();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [inviteCode, setInviteCode] = useState("");
  const textAreaRef = useRef(null);
  const oauthHandledRef = useRef(false);
  const { inviteesData } = useSelector(({ invite }) => invite);
  const { invitees, count } = inviteesData;
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [hasLoadedInvitees, setHasLoadedInvitees] = useState(false);
  const [searchTerm, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importInitialStep, setImportInitialStep] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [optOutOpen, setOptOutOpen] = useState(false);
  const [optingOut, setOptingOut] = useState(false);
  const [contactsStats, setContactsStats] = useState(null);
  const [contactsPreview, setContactsPreview] = useState({
    on_platform: [],
    invite: [],
  });
  const [contactsLoading, setContactsLoading] = useState(false);
  const [importQueued, setImportQueued] = useState(false);
  const importQueuedRef = useRef(false);
  const auth = useSelector(({ auth }) => auth);
  const userData = auth?.userData || {};
  const { first_name, last_name, username } = userData;

  const isInviteesTab = pathname.includes("invitees");
  const isContactsTab = pathname.includes("/invite/contacts");
  const prevInviteesTab = useRef(isInviteesTab);

  const openContactsPreview = (opts = {}) => {
    const {
      importedCount = 0,
      skipped = 0,
      fetched = 0,
      noEmail = 0,
      source = "google",
      showToast = true,
    } = opts;

    if (showToast) {
      try {
        trackContactsImportCompleted(
          source,
          importedCount || skipped || fetched
        );
      } catch (e) {
        // analytics must never block preview
      }

      if (importedCount > 0) {
        cogoToast.success(`Imported ${importedCount} contacts`);
      } else if (skipped > 0) {
        cogoToast.success(
          `${skipped} contact${skipped === 1 ? "" : "s"} ready to review`
        );
      } else if (fetched === 0 && noEmail > 0) {
        cogoToast.info(
          `Google returned contacts but none had email addresses (${noEmail} without email).`
        );
      }
    }

    setHasConsent(true);
    setImportInitialStep("results");
    setImportOpen(true);
  };

  // Open preview immediately after Google OAuth redirect (before paint)
  useLayoutEffect(() => {
    if (oauthHandledRef.current) return;

    const query =
      search ||
      (typeof window !== "undefined" ? window.location.search : "");
    const params = new URLSearchParams(query);
    const importStatus = params.get("import");

    const pendingPreview =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(CONTACTS_PREVIEW_KEY) === "1";

    if (!importStatus && !pendingPreview) return;

    oauthHandledRef.current = true;

    if (importStatus === "complete" || pendingPreview) {
      try {
        sessionStorage.setItem(CONTACTS_PREVIEW_KEY, "1");
      } catch (e) {
        // ignore
      }

      openContactsPreview({
        importedCount: parseInt(params.get("count") || "0", 10),
        skipped: parseInt(params.get("skipped") || "0", 10),
        fetched: parseInt(params.get("fetched") || "0", 10),
        noEmail: parseInt(params.get("no_email") || "0", 10),
        source: params.get("source") || "google",
        showToast: !!importStatus,
      });
    } else if (importStatus === "queued") {
      // Import is running in background — show immediate feedback
      cogoToast.info(
        "Google contacts are being imported. This usually takes a few seconds.",
        { hideAfter: 6 }
      );
      importQueuedRef.current = true;
      setImportQueued(true);
      try {
        sessionStorage.setItem("wb_import_queued", "1");
      } catch (e) {
        // ignore
      }
    } else if (importStatus === "rate_limited") {
      cogoToast.warn(
        `Google rate limit reached. Please retry in ${
          params.get("retry_after") || 60
        } seconds.`
      );
    } else if (importStatus === "denied") {
      cogoToast.error("Google authorization was denied");
    } else if (importStatus === "reauth_required") {
      cogoToast.error("Please reconnect your Google account");
    } else if (importStatus === "error") {
      cogoToast.error("Contact import failed. Please try again.");
    }

    if (importStatus) {
      history.replace(
        pathname.includes("/invite/contacts")
          ? pathname
          : "/invite/contacts"
      );
    }
  }, [search, pathname, history]);

  useEffect(() => {
    if (!isInviteesTab) {
      prevInviteesTab.current = false;
      return;
    }

    const tabJustOpened = !prevInviteesTab.current;
    prevInviteesTab.current = true;

    if (tabJustOpened && page !== 1) {
      setPage(1);
      setHasLoadedInvitees(false);
      return;
    }

    setLoadingMore(true);
    setFetchError("");

    if (page === 1) {
      dispatch({ type: RESET_INVITES });
    }

    Promise.resolve(getInviteAction(dispatch, page)).then((result) => {
      if (result?.type === ERROR) {
        setFetchError(result.payload || "Failed to load invitees");
      }
      setHasLoadedInvitees(true);
      setLoadingMore(false);
    });
  }, [dispatch, page, isInviteesTab]);

  useEffect(() => {
    setInviteCode(username);
  }, [username]);

  useEffect(() => {
    contactImportService
      .getStats()
      .then((res) => {
        const data = res?.data ?? res;
        setHasConsent(!!data?.has_ndpa_consent);
      })
      .catch(() => {});
  }, []);

  const loadContactsTab = async () => {
    setContactsLoading(true);
    try {
      const [statsRes, contactsRes] = await Promise.all([
        contactImportService.getStats(),
        contactImportService.getContacts(),
      ]);
      const stats = statsRes?.data ?? statsRes;
      const contacts = contactsRes?.data ?? contactsRes;
      setContactsStats(stats || null);
      setHasConsent(!!stats?.has_ndpa_consent);
      setContactsPreview({
        on_platform: contacts?.on_platform || [],
        invite: contacts?.invite || [],
      });
    } catch (e) {
      // Keep prior data; empty state handles no contacts
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    if (isContactsTab) {
      loadContactsTab();
    }
  }, [isContactsTab]);

  // Poll for contacts while a background Google import is queued
  useEffect(() => {
    const isQueued =
      importQueued ||
      (typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("wb_import_queued") === "1");

    if (!isContactsTab || !isQueued) return;

    importQueuedRef.current = true;
    setImportQueued(true);

    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 30 × 4s = 2 minutes max

    const poll = setInterval(async () => {
      attempts++;
      try {
        const [statsRes, contactsRes] = await Promise.all([
          contactImportService.getStats(),
          contactImportService.getContacts(),
        ]);
        const stats = statsRes?.data ?? statsRes;
        const contacts = contactsRes?.data ?? contactsRes;
        const total =
          (contacts?.on_platform?.length || 0) +
          (contacts?.invite?.length || 0);

        if (total > 0 || attempts >= MAX_ATTEMPTS) {
          clearInterval(poll);
          importQueuedRef.current = false;
          setImportQueued(false);
          try {
            sessionStorage.removeItem("wb_import_queued");
          } catch (e) {
            // ignore
          }
          setContactsStats(stats || null);
          setHasConsent(!!stats?.has_ndpa_consent);
          setContactsPreview({
            on_platform: contacts?.on_platform || [],
            invite: contacts?.invite || [],
          });
          if (total > 0) {
            cogoToast.success(
              `${total} contact${total === 1 ? "" : "s"} imported from Google!`
            );
          } else {
            cogoToast.warn(
              "Import finished but no contacts were found. Try importing again."
            );
          }
        }
      } catch (e) {
        // network hiccup — keep polling
      }
    }, 4000);

    return () => clearInterval(poll);
  }, [isContactsTab, importQueued]);

  const openImportFlow = () => {
    trackGrowNetworkCardClick("invite_tab");
    setImportInitialStep(null);
    if (hasConsent) {
      setImportOpen(true);
    } else {
      setConsentOpen(true);
    }
  };

  const openInviteFromContacts = () => {
    trackGrowNetworkCardClick("invite_contacts_tab");
    setImportInitialStep("results");
    if (hasConsent) {
      setImportOpen(true);
    } else {
      setConsentOpen(true);
    }
  };

  const handleConsent = () => {
    setConsentOpen(false);
    setImportOpen(true);
  };

  const handleOptOut = async () => {
    setOptingOut(true);
    try {
      await contactImportService.deleteAllContacts();
      setHasConsent(false);
      setContactsStats(null);
      setContactsPreview({ on_platform: [], invite: [] });
      setOptOutOpen(false);
      cogoToast.success(
        "All imported contacts deleted. You can import again anytime."
      );
    } catch (e) {
      cogoToast.error(
        e?.response?.data?.message || "Failed to delete contacts"
      );
    } finally {
      setOptingOut(false);
    }
  };

  const closeImportModal = () => {
    setImportOpen(false);
    setImportInitialStep(null);
    try {
      sessionStorage.removeItem(CONTACTS_PREVIEW_KEY);
    } catch (e) {
      // ignore
    }
    if (isContactsTab) {
      loadContactsTab();
    }
  };

  const copyToClipboard = () => {
    textAreaRef.current.select();
    document.execCommand("copy");
    setCopied(true);
    trackInviteCopyLinkClick({
      invite_code: inviteCode,
      source: "invite_page",
    });
  };

  const uniqueURL = `${process.env.REACT_APP_WEB_URL}/signup/${inviteCode}`;
  const title = `${first_name} ${last_name} will like you to join workbrook. Please follow the link to download workbrook`;
  const hashtags = ["workbrook"];

  const showPreviewFallback =
    (search && search.includes("import=complete")) ||
    (typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(CONTACTS_PREVIEW_KEY) === "1");

  const displaySection = () => {
    if (isContactsTab) {
      const totalContacts =
        (contactsPreview.on_platform?.length || 0) +
        (contactsPreview.invite?.length || 0);
      const previewRows = [
        ...(contactsPreview.on_platform || []).slice(0, 8).map((c) => ({
          ...c,
          _kind: "on_platform",
        })),
        ...(contactsPreview.invite || []).slice(0, 12).map((c) => ({
          ...c,
          _kind: "invite",
        })),
      ].slice(0, 12);

      return (
        <div className="m-3">
          {contactsLoading && !contactsStats && (
            <div className="text-center my-5">
              <ButtonLoader />
            </div>
          )}
          {!contactsLoading && totalContacts === 0 && importQueued && (
            <div
              className="text-center py-4 px-3"
              style={{
                background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                borderRadius: 12,
                border: "1px solid #C7D2FE",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "#3A6ED4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  animation: "wb-pulse 1.4s ease-in-out infinite",
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"
                    fill="white"
                    opacity="0"
                  />
                  <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="2.5" fill="none" strokeDasharray="40" strokeDashoffset="10" />
                </svg>
              </div>
              <p
                style={{
                  fontWeight: 600,
                  color: "#3730A3",
                  marginBottom: 4,
                  fontSize: 15,
                }}
              >
                Importing your Google contacts…
              </p>
              <p className="small text-muted mb-0">
                This usually takes a few seconds. The list will update
                automatically when ready.
              </p>
              <div
                style={{
                  marginTop: 14,
                  height: 4,
                  borderRadius: 99,
                  background: "#C7D2FE",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: "40%",
                    background: "#3A6ED4",
                    borderRadius: 99,
                    animation: "wb-slide 1.6s ease-in-out infinite",
                  }}
                />
              </div>
            </div>
          )}
          {!contactsLoading && totalContacts === 0 && !importQueued && (
            <div className="text-center py-4">
              <EmptyState text="No imported contacts yet" />
              <p className="small text-muted mt-2 mb-3">
                Import Google or CSV contacts to grow your network and track
                invite progress here.
              </p>
              <CustomButton
                backgroundColor="#3A6ED4"
                color="#fff"
                text="Import your contacts"
                handleClick={openImportFlow}
                additionalClass="mx-auto"
              />
            </div>
          )}
          {(contactsStats || totalContacts > 0) && (
            <>
              <NetworkGrowthSummary
                stats={contactsStats || {}}
                onImportMore={openImportFlow}
                onInviteMore={openInviteFromContacts}
                onOptOut={() => setOptOutOpen(true)}
              />
              {totalContacts > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                    <h6 className="mb-2 mb-md-0" style={{ color: "#0F172A" }}>
                      Imported contacts ({totalContacts})
                    </h6>
                    <CustomButton
                      text="Invite Contacts"
                      backgroundColor="#3A6ED4"
                      color="#fff"
                      handleClick={openInviteFromContacts}
                    />
                  </div>
                  <div className="gn-results-list" style={{ maxHeight: 320 }}>
                    {previewRows.map((c) => {
                      const label = c.email || c.phone || "";
                      const name =
                        c._kind === "on_platform" && c.profile?.first_name
                          ? `${c.profile.first_name} ${c.profile.last_name || ""}`
                          : c.name || label;
                      return (
                        <div key={c.id} className="gn-contact-row">
                          <div className="gn-contact-info">
                            <div className="name">{name}</div>
                            <div className="email">
                              {label}
                              {c._kind === "on_platform"
                                ? " · On Workbrook"
                                : c.invite_channel === "sms" || (!c.email && c.phone)
                                  ? " · SMS"
                                  : " · Email"}
                              {c.already_invited_by_self ? " · Invited" : ""}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {totalContacts > previewRows.length && (
                    <p className="small text-muted mt-2 mb-0">
                      Showing {previewRows.length} of {totalContacts}. Click
                      Invite Contacts to select people and send invites.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    if (isInviteesTab) {
      return (
        <div className="m-3">
          {loadingMore && page === 1 && (
            <div className="text-center my-5">
              <ButtonLoader />
            </div>
          )}
          {fetchError && (
            <p className="text-danger text-center my-4">{fetchError}</p>
          )}
          {!loadingMore &&
            hasLoadedInvitees &&
            !fetchError &&
            !invitees[0] && <EmptyState text="you have not invited anyone" />}
          {invitees[0] && (
            <div>
              <div className="mb-3">
                <SearchInput
                  handleChange={(e) => setSearch(e.target.value)}
                  value={searchTerm}
                />
              </div>
              {invitees
                .filter((invitee) => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return (
                    `${invitee.first_name} ${invitee.last_name}`
                      .toLowerCase()
                      .includes(q) ||
                    (invitee.username || "").toLowerCase().includes(q)
                  );
                })
                .map((invitee) => (
                  <span
                    key={invitee.id || invitee.username}
                    className="d-flex mb-3 align-items-center"
                  >
                    <img
                      width="40"
                      height="40"
                      className="rounded-circle"
                      src={
                        invitee.image_url ||
                        "https://res.cloudinary.com/dllrkis3c/image/upload/v1592308594/default_avatar.png"
                      }
                      alt=""
                    />
                    <span className="d-flex flex-column ml-3">
                      <h6 className="mb-0">
                        {invitee.first_name} {invitee.last_name}
                        {String(invitee.status) === "0" && (
                          <span
                            className="badge badge-warning ml-2"
                            style={{ fontSize: "0.65rem", fontWeight: 500 }}
                          >
                            Pending activation
                          </span>
                        )}
                      </h6>
                      <span className="co-gray">@{invitee.username}</span>
                    </span>
                  </span>
                ))}
              {count > 10 * page && (
                <div className="mx-auto mt-3 w-75">
                  <CustomButton
                    handleClick={() => setPage(page + 1)}
                    text={loadingMore ? <ButtonLoader /> : "Load More"}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <h5 className="text-center mt-5">YOUR UNIQUE URL</h5>
        <p className="mx-auto w-75 text-center my-5">
          <textarea
            ref={textAreaRef}
            value={uniqueURL}
            className="borderless w-100 copy-link"
            readOnly
          />
        </p>
        {!copied && (
          <div className="w-75 my-3 d-flex flex-column flex-md-row mx-auto">
            <CustomButton
              backgroundColor="#3A6ED4"
              color="#fff"
              text="Copy Link"
              handleClick={copyToClipboard}
              additionalClass="m-2"
            />
          </div>
        )}
        {copied && (
          <div className="w-75 my-3 d-flex flex-column flex-md-row mx-auto">
            <CustomButton
              borderColor="#3A6ED4"
              backgroundColor="#fff"
              color="#3A6ED4"
              text="Link Copied"
              additionalClass="m-2"
            />
          </div>
        )}
        <div className="text-center mb-4 mt-4">
          <h5 className="mb-2">Click to share your Unique URL</h5>
          <div>
            <FacebookShareButton
              url={uniqueURL}
              quote={title}
              className="mr-2"
              hashtag="#workbrook #job"
            >
              <FacebookIcon size={32} round />
            </FacebookShareButton>

            <TwitterShareButton
              url={uniqueURL}
              title={title}
              className="mr-2"
              hashtags={hashtags}
            >
              <TwitterIcon size={32} round />
            </TwitterShareButton>

            <LinkedinShareButton url={uniqueURL} className="mr-2">
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>

            <EmailShareButton
              url={uniqueURL}
              subject="You have been invited to join workbrook"
              body={title}
            >
              <EmailIcon size={32} round />
            </EmailShareButton>
          </div>
          <p className="mx-auto w-75 text-center mt-4 mb-0 small text-muted">
            By sharing your invite link, you agree that your username and invite
            count may appear on the workbrook leaderboard.
          </p>
          <div className="text-center mt-4">
            <CustomButton
              backgroundColor="#3A6ED4"
              color="#fff"
              text="Import your contacts"
              handleClick={openImportFlow}
              additionalClass="mx-auto"
            />
            <p className="small text-muted mt-2 mb-0">
              Grow your network faster by importing Google or CSV contacts
            </p>
            {showPreviewFallback && (
              <div className="mt-3">
                <CustomButton
                  backgroundColor="#fff"
                  color="#3A6ED4"
                  borderColor="#3A6ED4"
                  text="View imported contacts"
                  handleClick={() => openContactsPreview({ showToast: false })}
                  additionalClass="mx-auto"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="d-flex flex-column flex-md-row mt-3">
        <div className="sub-menu-container">
          <BackButton />
          <SubMenu menuOptions={subMenuOptions} includeSVG />
        </div>
        <div className="main-content ml-md-4">
          <Card className="mt-5">{displaySection()}</Card>
        </div>
      </div>
      <NdpaConsentModal
        isOpen={consentOpen}
        onClose={() => setConsentOpen(false)}
        onConsent={handleConsent}
        loading={false}
      />
      <CustomModal
        isOpen={optOutOpen}
        onClose={() => !optingOut && setOptOutOpen(false)}
        title="Opt out and delete contacts"
        maxWidth="480px"
      >
        <p>
          This removes all imported contacts and related invite records, and
          withdraws your import consent. You will be asked for consent again
          before the next import.
        </p>
        <p className="small text-muted">This cannot be undone.</p>
        <div className="d-flex justify-content-end">
          <CustomButton
            text="Cancel"
            backgroundColor="#fff"
            color="#64748B"
            borderColor="#CBD5E1"
            handleClick={() => setOptOutOpen(false)}
            additionalClass="mr-2"
            disabled={optingOut}
          />
          <CustomButton
            text={optingOut ? "Deleting..." : "Delete all contacts"}
            backgroundColor="#E24329"
            color="#fff"
            handleClick={handleOptOut}
            disabled={optingOut}
          />
        </div>
      </CustomModal>
      <ImportContactsModal
        isOpen={importOpen}
        onClose={closeImportModal}
        returnPath="/invite/contacts"
        initialStep={importInitialStep}
        onComplete={() => {
          loadContactsTab();
          if (!pathname.includes("/invite/contacts")) {
            history.push("/invite/contacts");
          }
        }}
      />
    </Layout>
  );
};

export default Invite;
